<template>
  <v-row class="justify-center">
    <select-policy-dialog ref="selectPolicyDialog" :household="household" @on-policy-updated="updateSavedPolicy" @on-select="selectPolicy" />

    <v-dialog
      v-model="intakeDialog"
      max-width="1000px"
      persistent
    >
      <v-card>
        <v-card-title>Find Money</v-card-title>
        <v-card-text class="px-0">
          <intake-summary :household="household" @on-save="intakeSummaryUpdate" @on-close="intakeDialog = false" />
        </v-card-text>
      </v-card>
    </v-dialog>

    <loading-plan-dialog ref="loadingPlanDialog" />

    <v-dialog
      v-model="truthCalculatorDialog"
      max-width="600px"
      content-class="elevation-0"
    >
      <truth-calculator />
    </v-dialog>

    <v-col
      md="8"
      lg="9"
    >
      <v-row class="align-center justify-center mb-4">
        <v-col cols="3" class="py-0">
          <v-card flat class="error" style="height: 120px;">
            <v-card-text class="py-2 align-center justify-center d-flex flex-column fill-height white--text">
              <div class="subtitle-2 text-center">Paid Off In</div>
              <div class="display-1 text-center">{{ paidOff }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="3" class="py-0">
          <v-card flat class="error" style="height: 120px;">
            <v-card-text class="py-2 align-center justify-center d-flex flex-column fill-height white--text">
              <div class="subtitle-2 text-center">Total Interest Cost</div>
              <div class="display-1 text-center">{{ plan.data.total_min_interest | currencyRounded }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="3" class="py-0">
          <v-card flat class="error" style="height: 120px;">
            <v-card-text class="py-2 align-center justify-center d-flex flex-column fill-height white--text">
              <div class="subtitle-2 text-center mb-2">Interest Volume</div>
              <div class="display-1 text-center">{{ interestVolume }}</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row v-if="plan.data.debts" class="mb-4">
        <v-col cols="12">
          <debt-chart :plan="plan" min-only />
        </v-col>
      </v-row>

      <v-divider class="mb-4 mt-8" />

      <v-row class="comparisonContainer">
        <v-col cols="12" class="pa-0">
          <v-row no-gutters class="mb-8">
            <v-col cols="12" >
              <debt-table :plan="plan" min @on-open-debt="$emit('on-open-min-debt', $event)" />
            </v-col>
          </v-row>
        </v-col>
        
      </v-row>

    </v-col>

    <v-col
      md="4"
      lg="3"
    >
      <v-row no-gutters class="mb-6 flex-column">
        <v-col class="mb-2">
          <v-btn class="elevation-0" x-large block color="primary" @click="$refs.selectPolicyDialog.open()"><v-icon small class="mr-2">mdi-plus</v-icon> Add A Policy</v-btn>
        </v-col>
        <v-col class="my-2">
          <v-btn class="elevation-0" x-large outlined block color="primary" @click="intakeDialog = true"><v-icon small class="mr-2">mdi-file-pdf</v-icon> Find Money</v-btn>
        </v-col>
        <v-col class="my-2">
          <v-btn class="elevation-0" x-large outlined block color="primary" @click="truthCalculatorDialog = true"><v-icon small class="mr-2">mdi-calculator</v-icon> Truth Calculator</v-btn>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>
<script>
import DebtChart from './DebtChart'
import DebtTable from './DebtTable'
import SelectPolicyDialog from './SelectPolicyDialog'
import IntakeSummary from '@/views/households/IntakeSummary'
import LoadingPlanDialog from './LoadingPlanDialog'
import TruthCalculator from '@/views/tools/TruthCalculator'

export default {
  name: 'MinPlan',
  props: {
    household: {
      type: Object,
      required: true
    },
    plan: {
      type: Object,
      required: true
    }
  },
  components: {
    DebtChart,
    DebtTable,
    SelectPolicyDialog,
    IntakeSummary,
    LoadingPlanDialog,
    TruthCalculator
  },
  data() {
    return {
      selecting: false,
      addPolicyDialog: false,
      intakeDialog: false,
      truthCalculatorDialog: false
    }
  },
  computed: {
    paidOff() {
      return this.summaryTime(this.plan.data.min_freedom_months)
    },
    interestVolume() {
      const interest = Math.round(this.plan.data.total_min_interest / this.plan.data.total_debt_principal * 100);
      return isNaN(interest) ? '-' : (interest + '%');
    },
    policiesSorted () {
      let p = JSON.parse(JSON.stringify(this.household.policies))
      p.sort((a,b) => (a.active - b.active))
      return p
    },
    debtIds () {
      return this.household.debts.map(d => {
        return d.id
      })
    }
  },
  methods: {
    summaryTime(months) {
      if(isNaN(months)) return '-'
      if(months < 12) {
        return months + (months !== 1 && 's')
      }
      const years = Math.round(months / 12 * 10) / 10
      return years + ' Year' + (years !== 1 && 's')
    },
    updateSavedPolicy (policy) {
      this.$emit('on-policy-updated', policy)
      this.selectPolicy(policy)
    },
    selectPolicy(policy) {
      if(this.selecting) return
      this.selecting            = true
      this.addPolicyDialog      = false
      this.intakeDialog         = false
      this.$refs.loadingPlanDialog.open()
      let data = {
        debtsToUse: this.debtIds,
        policiesToUse: policy.id,
        algorithm: 'balance_low_high',
        type: 'freedom_accelerator',
        pipeline_value: policy.pipeline_value,
        default: 1
      }

      this.$http.post('households/' + this.household.id + '/plan', data)
        .then(response => {
          this.$emit('on-new-plan', response.data)
        })
        .catch(() => {
        })
        .finally(() => {
          this.loadingPlanDialog.close()
          this.selecting = false
        })
    },
    intakeSummaryUpdate(household) {
      this.$emit('on-update-intake', household)
      this.intakeDialog = false
    }
  }
}
</script>